import React, { useEffect, useMemo, useState } from 'react';
import { Radar } from '@ant-design/plots';
import { Button, Space, Tag, Typography } from 'antd';
import {
  BENCHMARK_BAND_LABELS,
  resolveBenchmarkRangesForMetric,
} from '../../helpers/metricRules';
import {
  BENCHMARK_FIELD_LABELS,
  BenchmarkField,
  DEFAULT_BENCHMARK_FIELDS,
  buildBenchmarkMetricCandidateIndex,
  extractBaseMetricKey,
  findBenchmarkMetricCandidates,
  formatBenchmarkValue,
  getBenchmarkFieldValue,
  normalizeBenchmarkField,
  resolveBenchmarkBand,
} from './benchmarkUtils';

interface Props {
  section: any;
  data: any[];
  areas: any[];
  metrics: any[];
  previewAreaId: number;
  dateRange?: [any, any];
}

type RadarRow = {
  metric: string;
  score: number;
  series: string;
  benchmark: string;
  valueLabel: string;
};

type RadarDisplayRow = RadarRow & {
  colorKey: string;
};

const BENCHMARK_SCORE_TICKS = [0, 1, 2, 3, 4, 5] as const;
const BENCHMARK_SCORE_DOMAIN: [number, number] = [0, 5];
const RADAR_SERIES_COLORS = ['#1677ff', '#52c41a', '#fa8c16', '#eb2f96', '#722ed1', '#13c2c2', '#f5222d', '#faad14'];
const BENCHMARK_SCALE_ITEMS = [
  { score: 1, label: 'Poor', color: '#ff4d4f' },
  { score: 2, label: 'Fair', color: '#fa8c16' },
  { score: 3, label: 'Good', color: '#faad14' },
  { score: 4, label: 'Great', color: '#7cb305' },
  { score: 5, label: 'Perfect', color: '#52c41a' },
] as const;
const RADAR_LABEL_COLOR = 'rgba(30, 41, 59, 0.92)';
const RADAR_METRIC_LABEL_SIZE = 14;
const RADAR_BAND_LABEL_SIZE = 12;
const RADAR_METRIC_LABEL_ROTATE = 0;
const RADAR_METRIC_LABEL_SPACING = 18;
const RADAR_BAND_LABEL_DY = -8;
const stripSeriesAreaId = (value: string) => value.replace(/\s+\(\d+\)$/, '');

const toRgba = (hexColor: string, alpha: number) => {
  const hex = hexColor.replace('#', '').trim();
  if (hex.length === 3) {
    const r = parseInt(hex[0] + hex[0], 16);
    const g = parseInt(hex[1] + hex[1], 16);
    const b = parseInt(hex[2] + hex[2], 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  if (hex.length === 6) {
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return `rgba(22, 119, 255, ${alpha})`;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const logBenchmarkRadarDebug = (_payload: any) => {
  // debug logging disabled in production
};

const parseBandLabel = (value: string) => {
  const numeric = Math.round(Number(value));
  const key = String(numeric) as keyof typeof BENCHMARK_BAND_LABELS;
  return BENCHMARK_BAND_LABELS[key] || value;
};

export const ReportBenchmarkRadar: React.FC<Props> = ({
  section,
  data,
  areas,
  metrics,
  previewAreaId,
  dateRange,
}) => {
  const [activeSeries, setActiveSeries] = useState<string | null>(null);
  const compareChildrenEnabled = section?.showChildren !== false;

  const selectedField = useMemo<BenchmarkField>(() => {
    const normalized = (Array.isArray(section?.fields) ? section.fields : [])
      .map((field: string) => normalizeBenchmarkField(String(field)))
      .filter((field: BenchmarkField | null): field is BenchmarkField => field !== null);
    return normalized[0] || DEFAULT_BENCHMARK_FIELDS[0];
  }, [section?.fields]);

  const metricByKey = useMemo(() => {
    const map = new Map<string, any>();
    (Array.isArray(metrics) ? metrics : []).forEach((metric: any) => {
      if (metric?.key) map.set(metric.key, metric);
    });
    return map;
  }, [metrics]);

  const metricCandidateIndex = useMemo(
    () => buildBenchmarkMetricCandidateIndex(Array.isArray(metrics) ? metrics : []),
    [metrics]
  );

  const areaById = useMemo(() => {
    const map = new Map<number, any>();
    (Array.isArray(areas) ? areas : []).forEach((area: any) => {
      if (area?.id) map.set(area.id, area);
    });
    return map;
  }, [areas]);

  const radarRows = useMemo<RadarRow[]>(() => {
    const rows = Array.isArray(data) ? data : [];
    return rows
      .map((row: any) => {
        const metricKey = row?.metricKey || section?.metrics?.[0];
        const metric = metricByKey.get(metricKey);
        const areaId = row?.areaId || previewAreaId;
        const area = areaById.get(areaId);
        const numericValue = getBenchmarkFieldValue(row, selectedField);
        const direct = resolveBenchmarkRangesForMetric(metric, area);
        logBenchmarkRadarDebug({
          stage: 'direct-check',
          rowMetricKey: row?.metricKey,
          metricKey: metric?.key,
          metricName: metric?.name,
          areaId,
          areaTypeId: area?.areaType?.id ?? area?.areaTypeId ?? null,
          selectedField,
          numericValue,
          directRanges: direct,
        });
        let ranges = direct;
        if (!ranges || Object.keys(ranges).length === 0) {
          const candidates = findBenchmarkMetricCandidates(metricCandidateIndex, [
            row?.metricKey,
            metric?.key,
            metric?.sourceKey,
            metric?.name,
            extractBaseMetricKey(row?.metricKey),
            extractBaseMetricKey(metric?.key),
          ]);
          logBenchmarkRadarDebug({
            stage: 'fallback-search',
            rowMetricKey: row?.metricKey,
            metricKey: metric?.key,
            metricName: metric?.name,
            areaId,
            areaTypeId: area?.areaType?.id ?? area?.areaTypeId ?? null,
            candidateKeys: candidates.map((candidate: any) => candidate?.key),
          });
          for (const candidate of candidates) {
            const next = resolveBenchmarkRangesForMetric(candidate, area);
            if (next && Object.keys(next).length > 0) {
              ranges = next;
              logBenchmarkRadarDebug({
                stage: 'fallback-hit',
                rowMetricKey: row?.metricKey,
                selectedMetricKey: candidate?.key,
                selectedMetricName: candidate?.name,
                areaId,
                areaTypeId: area?.areaType?.id ?? area?.areaTypeId ?? null,
                ranges: next,
              });
              break;
            }
          }
        }
        const benchmark = resolveBenchmarkBand(numericValue, ranges);
        logBenchmarkRadarDebug({
          stage: 'value-band-check',
          rowMetricKey: row?.metricKey,
          metricKey: metric?.key,
          metricName: metric?.name,
          areaId,
          areaTypeId: area?.areaType?.id ?? area?.areaTypeId ?? null,
          selectedField,
          numericValue,
          ranges,
          benchmark,
        });

        if (!metricKey || !metric || !benchmark) return null;

        return {
          metric: metric?.name || metricKey,
          score: benchmark.score,
          series: area?.name ? `${area.name} (${areaId})` : `Area ${areaId}`,
          benchmark: benchmark.label,
          valueLabel: formatBenchmarkValue(numericValue, row?.unit),
        } as RadarRow;
      })
      .filter((row: RadarRow | null): row is RadarRow => row !== null);
  }, [
    areaById,
    data,
    metricByKey,
    metricCandidateIndex,
    previewAreaId,
    section?.metrics,
    selectedField,
  ]);

  const selectedAreaName = useMemo(() => {
    return areaById.get(previewAreaId)?.name || null;
  }, [areaById, previewAreaId]);

  const dateRangeLabel = useMemo(() => {
    if (!Array.isArray(dateRange) || dateRange.length < 2) return null;
    const [start, end] = dateRange;
    const startLabel = start?.format ? start.format('YYYY-MM-DD') : null;
    const endLabel = end?.format ? end.format('YYYY-MM-DD') : null;
    if (!startLabel || !endLabel) return null;
    return `${startLabel} → ${endLabel}`;
  }, [dateRange]);

  const uniqueSeries = useMemo(() => {
    return Array.from(new Set(radarRows.map((row) => row.series)));
  }, [radarRows]);

  const previewSeries = useMemo(() => {
    return uniqueSeries.find((series) => series.endsWith(`(${previewAreaId})`)) || null;
  }, [previewAreaId, uniqueSeries]);

  const focusedSeries = useMemo(() => {
    if (activeSeries) return activeSeries;
    if (previewSeries) return previewSeries;
    if (uniqueSeries.length === 1) return uniqueSeries[0];
    return null;
  }, [activeSeries, previewSeries, uniqueSeries]);

  const visibleMetricLabels = useMemo(() => {
    return Array.from(new Set(radarRows.map((row) => row.metric)));
  }, [radarRows]);

  useEffect(() => {
    if (activeSeries && !uniqueSeries.includes(activeSeries)) {
      setActiveSeries(null);
    }
  }, [activeSeries, uniqueSeries]);

  useEffect(() => {
    if (!compareChildrenEnabled && activeSeries !== null) {
      setActiveSeries(null);
    }
  }, [compareChildrenEnabled, activeSeries]);

  const seriesColorMap = useMemo(() => {
    const map = new Map<string, string>();
    uniqueSeries.forEach((series, index) => {
      map.set(series, RADAR_SERIES_COLORS[index % RADAR_SERIES_COLORS.length]);
    });
    return map;
  }, [uniqueSeries]);

  const radarSeriesRange = useMemo(
    () =>
      uniqueSeries.map((series) => {
        const baseColor = seriesColorMap.get(series) || RADAR_SERIES_COLORS[0];
        if (!activeSeries || activeSeries === series) return baseColor;
        return 'rgba(217,217,217,0.1)';
      }),
    [activeSeries, uniqueSeries, seriesColorMap]
  );

  const displayRows = useMemo<RadarDisplayRow[]>(
    () =>
      radarRows.map((row) => ({
        ...row,
        colorKey: compareChildrenEnabled && activeSeries && row.series !== activeSeries ? '__muted__' : row.series,
      })),
    [activeSeries, compareChildrenEnabled, radarRows]
  );

  const colorDomain = useMemo(() => {
    if (!activeSeries) return uniqueSeries;
    return ['__muted__', activeSeries];
  }, [activeSeries, uniqueSeries]);

  const colorRange = useMemo(() => {
    if (!activeSeries) return radarSeriesRange;
    return ['rgba(217,217,217,0.1)', seriesColorMap.get(activeSeries) || RADAR_SERIES_COLORS[0]];
  }, [activeSeries, radarSeriesRange, seriesColorMap]);

  const config: any = useMemo(
    () => ({
      data: displayRows,
      height: 420,
      padding: [12, 12, 18, 12],
      xField: 'metric',
      yField: 'score',
      seriesField: uniqueSeries.length > 1 ? 'series' : undefined,
      colorField: uniqueSeries.length > 1 ? 'colorKey' : undefined,
      color: uniqueSeries.length > 1 ? undefined : RADAR_SERIES_COLORS[0],
      line: {
        style: {
          lineWidth: 3,
        },
      },
      area: {
        style: {
          fillOpacity: uniqueSeries.length > 1 ? 0.06 : 0.08,
        },
      },
      point: {
        size: 5,
        style: {
          lineWidth: 2,
          stroke: '#ffffff',
        },
      },
      scale: {
        y: {
          type: 'linear',
          domain: BENCHMARK_SCORE_DOMAIN,
          domainMin: BENCHMARK_SCORE_DOMAIN[0],
          domainMax: BENCHMARK_SCORE_DOMAIN[1],
          tickCount: BENCHMARK_SCORE_TICKS.length,
          tickMethod: () => [...BENCHMARK_SCORE_TICKS],
          nice: false,
          clamp: true,
        },
        color:
          uniqueSeries.length > 1
            ? {
                domain: colorDomain,
                range: colorRange,
              }
            : undefined,
      },
      axis: {
        x: {
          line: false,
          labelAutoRotate: false,
          labelRotate: RADAR_METRIC_LABEL_ROTATE,
          labelSpacing: RADAR_METRIC_LABEL_SPACING,
          labelFill: RADAR_LABEL_COLOR,
          labelFontSize: RADAR_METRIC_LABEL_SIZE,
          labelFontWeight: 600,
        },
        y: {
          tickCount: BENCHMARK_SCORE_TICKS.length,
          tickFilter: (value: string | number) =>
            BENCHMARK_SCORE_TICKS.includes(Math.round(Number(value)) as (typeof BENCHMARK_SCORE_TICKS)[number]),
          label: true,
          labelFormatter: (value: string | number) => {
            const numeric = Math.round(Number(value));
            if (numeric <= 0) return '0 No data';
            return `${numeric} ${parseBandLabel(String(numeric))}`;
          },
          labelFill: 'rgba(51, 65, 85, 0.96)',
          labelFontSize: 12,
          labelFontWeight: 600,
          labelSpacing: 8,
          line: false,
          grid: true,
          gridStroke: 'rgba(100, 116, 139, 0.82)',
          gridLineWidth: 1.5,
          gridLineDash: [],
        },
      },
      legend: false,
      interaction: {
        tooltip: {
          crosshairs: false,
          crosshairsX: false,
          crosshairsY: false,
          marker: false,
        },
      },
      tooltip: {
        shared: false,
        marker: false,
        crosshairs: false,
        crosshairsX: false,
        crosshairsY: false,
        title: (datum: RadarRow) => datum.metric,
        items: [
          (datum: RadarRow) => ({
            name: datum.series,
            value: `${datum.benchmark} (${datum.valueLabel})`,
          }),
        ],
        customContent: (title: string, items: any[] = []) => {
          const seen = new Set<string>();
          const uniqueItems = items
            .map((item) => {
              const datum = item?.data || item?.datum || {};
              const normalizedName = String(datum?.series || item?.name || '');
              const normalizedValue =
                datum?.benchmark && datum?.valueLabel
                  ? `${datum.benchmark} (${datum.valueLabel})`
                  : String(item?.value || '');
              const normalizedColor = item?.color || '#1677ff';

              return {
                name: normalizedName,
                value: normalizedValue,
                color: normalizedColor,
              };
            })
            .filter((item) => {
              const key = `${item.name}::${item.value}`;
              if (seen.has(key)) return false;
              seen.add(key);
              return Boolean(item.name || item.value);
            });

          const rows = uniqueItems
            .map((item) => {
              return `
                <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;margin-top:8px;">
                  <div style="display:flex;align-items:center;gap:8px;min-width:0;">
                    <span style="width:8px;height:8px;border-radius:999px;background:${item.color};display:inline-block;flex-shrink:0;"></span>
                    <span style="font-size:12px;color:#334155;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${item?.name || ''}</span>
                  </div>
                  <span style="font-size:12px;font-weight:600;color:#0f172a;white-space:nowrap;">${item?.value || ''}</span>
                </div>
              `;
            })
            .join('');

          return `
            <div style="min-width:220px;padding:10px 12px;border-radius:12px;background:#ffffff;border:1px solid rgba(226,232,240,0.95);box-shadow:0 12px 24px rgba(15,23,42,0.12);">
              <div style="font-size:12px;font-weight:700;color:#64748b;">${title || ''}</div>
              ${rows}
            </div>
          `;
        },
      },
      animation: false,
    }),
    [displayRows, uniqueSeries.length, colorDomain, colorRange]
  );

  if (radarRows.length === 0) {
    return <Typography.Text type="secondary">No benchmark data</Typography.Text>;
  }

  return (
    <div className="report-benchmark-radar-workspace">
      <div className="report-benchmark-radar-head">
        <div className="report-benchmark-radar-title">{section?.title || 'Spider'}</div>
        <div className="report-benchmark-radar-meta">
          {[selectedAreaName, dateRangeLabel].filter(Boolean).join(' · ')}
        </div>
      </div>

      <div className="report-benchmark-radar-legend">
        <span className="report-benchmark-radar-chip">Field: {BENCHMARK_FIELD_LABELS[selectedField]}</span>
        <span className="report-benchmark-radar-chip report-benchmark-radar-chip--good">Outer ring = higher score</span>
        <span className="report-benchmark-radar-chip report-benchmark-radar-chip--poor">Center = no data</span>
      </div>

      <div className={`report-benchmark-radar-body ${compareChildrenEnabled ? '' : 'report-benchmark-radar-body--single'}`}>
        {compareChildrenEnabled ? (
          <aside className="report-benchmark-radar-rail">
            <div className="report-benchmark-radar-section-head">
              <div className="report-benchmark-radar-section-title">Areas</div>
              <div className="report-benchmark-radar-section-subtitle">
                {uniqueSeries.length <= 1 ? 'Only one area with benchmark data' : 'Focus one area or compare all'}
              </div>
            </div>

            <div className="report-benchmark-radar-series-list">
              <Button
                type={activeSeries === null ? 'primary' : 'default'}
                className="report-benchmark-radar-series-btn"
                onClick={() => setActiveSeries(null)}
              >
                Show all
              </Button>
              {uniqueSeries.map((series) => {
                const color = seriesColorMap.get(series) || RADAR_SERIES_COLORS[0];
                const isActive = activeSeries === series;
                return (
                  <button
                    key={series}
                    type="button"
                    onClick={() => setActiveSeries(series)}
                    className={`report-benchmark-radar-series-pill ${isActive ? 'report-benchmark-radar-series-pill--active' : ''}`}
                    style={{
                      borderColor: isActive ? color : undefined,
                      background: isActive ? toRgba(color, 0.14) : undefined,
                    }}
                  >
                    <span
                      className="report-benchmark-radar-series-dot"
                      style={{ background: color }}
                    />
                    <span>{series}</span>
                  </button>
                );
              })}
            </div>
          </aside>
        ) : null}

        <div className="report-benchmark-radar-main">
          <div className="report-benchmark-radar-stage">
            <div className="report-benchmark-radar-stage-head">
              <div>
                <div className="report-benchmark-radar-stage-title">Benchmark profile</div>
                <div className="report-benchmark-radar-stage-subtitle">
                  {focusedSeries
                    ? `${stripSeriesAreaId(focusedSeries)} · ${visibleMetricLabels.length} metrics`
                    : `${uniqueSeries.length} areas compared · hover a point for exact values`}
                </div>
              </div>
              {focusedSeries ? (
                <span className="report-benchmark-radar-focus-pill">
                  Focused: {stripSeriesAreaId(focusedSeries)}
                </span>
              ) : (
                <span className="report-benchmark-radar-focus-pill report-benchmark-radar-focus-pill--muted">
                  Compare all visible areas
                </span>
              )}
            </div>

            <div className="report-benchmark-radar-visual report-benchmark-radar-visual--single">
              <div className="report-benchmark-radar-chart-shell">
                <Radar {...config} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportBenchmarkRadar;
